/**
 * PrivateRoute.js
 *
 * SUBSCRIPTION ENFORCEMENT (this version)
 *   When the logged-in user is a company admin whose subscription has
 *   expired (user.subscription_locked === true, set by auth.controller.js
 *   at login/getMe time), every route EXCEPT /billing now force-redirects
 *   to /billing instead of rendering. This is what actually makes the
 *   backend-side lock (tenant.middleware.js + auth.controller.js) visible
 *   and useful to the admin — without this, they'd still be able to
 *   navigate the sidebar into pages that quietly fail every API call
 *   with a 402, which is confusing. Now they land directly on the one
 *   page that can actually fix the problem.
 *
 *   Employees never reach this component in a locked state at all —
 *   they're rejected outright at login (see auth.controller.js), so
 *   there's nothing for PrivateRoute to additionally enforce for them.
 *
 * All other existing behaviour (loading spinner, no-token redirect,
 * role-based access) is unchanged.
 */
import React, { useContext } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
const PrivateRoute = ({ children, allowedRoles }) => {
    const { user, loading } = useContext(AuthContext);
    const token    = localStorage.getItem('token');
    const location = useLocation();
    // Still verifying session — show spinner, do NOT redirect yet
    if (loading) {
        return (
            <div className="vh-100 d-flex flex-column align-items-center justify-content-center bg-light">
                <div className="spinner-border text-primary mb-3" role="status" aria-label="Loading"/>
                <div className="fw-bold text-primary">Verifying credentials…</div>
            </div>
        );
    }
    // No token at all → go to login
    if (!token) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }
    // Token exists but user hasn't loaded yet (edge case during refresh)
    if (!user) {
        return (
            <div className="vh-100 d-flex flex-column align-items-center justify-content-center bg-light">
                <div className="spinner-border text-primary mb-3" role="status"/>
                <div className="fw-bold text-primary">Loading your profile…</div>
            </div>
        );
    }
    // Role check — if no allowedRoles specified, any logged-in user is allowed
    if (allowedRoles && !allowedRoles.includes(user.role)) {
        // Redirect to appropriate home based on role
        const home = user.role === 'super-admin' ? '/superadmin/dashboard' : '/dashboard';
        return <Navigate to={home} replace />;
    }
    // SUBSCRIPTION ENFORCEMENT: an admin whose company's subscription has
    // expired is forced to /billing on every navigation until they renew.
    // /billing itself is excluded so the page they're being sent to can
    // actually render instead of redirecting to itself in a loop.
    if (user.subscription_locked && location.pathname !== '/billing') {
        return <Navigate to="/billing" replace />;
    }
    return children;
};
export default PrivateRoute;
